/*
 * product.js — product form (incl. quick-add), variant selection, media gallery,
 * sticky add-to-cart. Client-side variant matching from embedded JSON (no network).
 */
(function () {
  'use strict';
  const JF = (window.JF = window.JF || {});

  /* ---- <product-form> : async add to cart ---- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.submitButton = this.querySelector('[type="submit"]');
      this.errorEl = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    onSubmit(e) {
      e.preventDefault();
      if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') return;
      const idInput = this.form.querySelector('[name="id"]');
      if (!idInput || !idInput.value) return;
      const qtyInput = this.form.querySelector('[name="quantity"]');
      const item = { id: idInput.value, quantity: qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1 };
      const sp = this.form.querySelector('select[name="selling_plan"], [name="selling_plan"]:checked');
      if (sp && sp.value) item.selling_plan = sp.value;

      this.setLoading(true);
      if (this.errorEl) this.errorEl.hidden = true;

      JF.cart
        .add(item, this.submitButton)
        .catch((err) => {
          const msg = (err && (err.description || err.message)) || 'Une erreur est survenue.';
          if (this.errorEl) {
            this.errorEl.textContent = msg;
            this.errorEl.hidden = false;
          }
        })
        .finally(() => this.setLoading(false));
    }

    setLoading(state) {
      this.classList.toggle('is-loading', state);
      if (this.submitButton) {
        this.submitButton.classList.toggle('is-loading', state);
        this.submitButton.setAttribute('aria-busy', state ? 'true' : 'false');
      }
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* ---- <variant-selects> : client-side variant matching ---- */
  class VariantSelects extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-product-json]');
      if (!data) return;
      try {
        this.variants = JSON.parse(data.textContent);
      } catch (e) {
        this.variants = [];
      }
      this.sectionId = this.getAttribute('data-section-id');
      this.productUrl = this.getAttribute('data-url');
      this.addEventListener('change', this.onChange.bind(this));
      this.gallery = document.querySelector('media-gallery[data-section-id="' + this.sectionId + '"]');
    }

    selectedOptions() {
      const groups = Array.from(this.querySelectorAll('[data-option-position]'));
      // radios: pick :checked within each position; selects: use value
      const byPos = {};
      groups.forEach((el) => {
        const pos = el.getAttribute('data-option-position');
        if (el.tagName === 'SELECT') {
          byPos[pos] = el.value;
        } else if (el.type === 'radio' && el.checked) {
          byPos[pos] = el.value;
        }
      });
      return Object.keys(byPos)
        .sort()
        .map((k) => byPos[k]);
    }

    onChange() {
      const selected = this.selectedOptions();
      const match = this.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      this.updateVariant(match);
    }

    updateVariant(variant) {
      const root = this.closest('[data-product-root]') || document;
      const idInputs = root.querySelectorAll('[name="id"]');
      const atcs = root.querySelectorAll('[data-atc-button]');
      const priceBlocks = root.querySelectorAll('[data-price-block]');

      if (!variant) {
        atcs.forEach((atc) => {
          atc.setAttribute('aria-disabled', 'true');
          atc.classList.add('is-disabled');
          const t = atc.querySelector('[data-atc-text]');
          if (t) t.textContent = t.getAttribute('data-unavailable') || 'Indisponible';
        });
        // No matching variant → treat as unavailable (show « Prévenez-moi »).
        if (root.classList) root.classList.add('is-variant-unavailable');
        return;
      }

      idInputs.forEach((input) => { input.value = variant.id; });

      // URL
      if (this.productUrl && window.history.replaceState) {
        const url = new URL(this.productUrl, window.location.origin);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      // Availability + ATC (every form in this product root: main + sticky)
      atcs.forEach((atc) => {
        const atcText = atc.querySelector('[data-atc-text]');
        if (variant.available) {
          atc.removeAttribute('aria-disabled');
          atc.classList.remove('is-disabled');
          if (atcText) atcText.textContent = atcText.getAttribute('data-default') || 'Ajouter au panier';
        } else {
          atc.setAttribute('aria-disabled', 'true');
          atc.classList.add('is-disabled');
          if (atcText) atcText.textContent = atcText.getAttribute('data-soldout') || 'Épuisé';
        }
      });

      // « Prévenez-moi » (Stoq) : bascule ATC ↔ notify selon la dispo + maj du variant ciblé.
      if (root.classList) root.classList.toggle('is-variant-unavailable', !variant.available);
      root.querySelectorAll('[data-notify]').forEach((b) => { b.dataset.variantId = variant.id; });

      // Price (every price block: main + sticky)
      priceBlocks.forEach((priceBlock) => {
        const priceEl = priceBlock.querySelector('[data-price]');
        const compareEl = priceBlock.querySelector('[data-compare-price]');
        // Keep the unit price on the block so qty changes can recompute the line total.
        priceBlock.dataset.unitPrice = variant.price;
        priceBlock.dataset.unitCompare = variant.compare_at_price || 0;
        if (priceEl) priceEl.textContent = JF.money(variant.price);
        if (compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.textContent = JF.money(variant.compare_at_price);
            compareEl.hidden = false;
            priceBlock.classList.add('price--on-sale');
          } else {
            compareEl.hidden = true;
            priceBlock.classList.remove('price--on-sale');
          }
        }
      });

      // Re-apply qty × unit on blocks that display a line total (main price).
      jfRenderQtyTotals(root);

      // Media
      if (this.gallery && variant.featured_media && typeof this.gallery.setActiveMedia === 'function') {
        this.gallery.setActiveMedia(variant.featured_media.id);
      }

      document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant: variant } }));
    }
  }
  if (!customElements.get('variant-selects')) customElements.define('variant-selects', VariantSelects);

  /* ---- <media-gallery> : thumbnail switching ---- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.viewer = this.querySelector('[data-gallery-viewer]');
      this.querySelectorAll('[data-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          this.setActiveMedia(thumb.getAttribute('data-media-id'));
        });
      });
    }
    setActiveMedia(mediaId) {
      const target = this.querySelector('[data-media-item][data-media-id="' + mediaId + '"]');
      if (!target) return;
      this.querySelectorAll('[data-media-item]').forEach((m) => m.classList.toggle('is-active', m === target));
      this.querySelectorAll('[data-thumb]').forEach((t) =>
        t.setAttribute('aria-current', t.getAttribute('data-media-id') === String(mediaId) ? 'true' : 'false')
      );
      if (target.scrollIntoView) target.scrollIntoView({ behavior: JF.reduceMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
    }
  }
  if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);

  /* ---- <bundle-add> : add several products to cart in one click ---- */
  class BundleAdd extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button, [type="submit"], .btn');
      if (!this.button) return;
      this.button.addEventListener('click', this.onClick.bind(this));
    }
    onClick(e) {
      e.preventDefault();
      let items = [];
      try {
        items = JSON.parse(this.getAttribute('data-bundle-items') || '[]');
      } catch (err) {
        items = [];
      }
      if (items.length && window.JF && JF.cart) {
        JF.cart.add(items, this.button);
      }
    }
  }
  if (!customElements.get('bundle-add')) customElements.define('bundle-add', BundleAdd);

  /* ---- quantity → live line total : main price shows qty × unit ---- */
  function jfMainQty(root) {
    // Visible stepper input (inside <quantity-input>); ignores the sticky hidden qty=1.
    const input = root.querySelector('quantity-input [name="quantity"]');
    const n = input ? parseInt(input.value, 10) : 1;
    return n > 0 ? n : 1;
  }

  function jfRenderQtyTotals(root) {
    const qty = jfMainQty(root);
    root.querySelectorAll('[data-qty-total]').forEach((block) => {
      const unit = parseFloat(block.dataset.unitPrice);
      const unitCompare = parseFloat(block.dataset.unitCompare);
      const priceEl = block.querySelector('[data-price]');
      const compareEl = block.querySelector('[data-compare-price]');
      if (priceEl && !isNaN(unit)) priceEl.textContent = JF.money(unit * qty);
      if (compareEl && !compareEl.hidden && !isNaN(unitCompare) && unitCompare > unit) {
        compareEl.textContent = JF.money(unitCompare * qty);
      }
    });
  }

  document.querySelectorAll('[data-product-root]').forEach((root) => {
    const qtyInput = root.querySelector('quantity-input [name="quantity"]');
    if (!qtyInput) return;
    const update = () => jfRenderQtyTotals(root);
    qtyInput.addEventListener('change', update); // stepper buttons dispatch 'change'
    qtyInput.addEventListener('input', update); // manual typing
    update(); // normalize on load (qty=1 ⇒ unit price)
  });

  /* ---- sticky add-to-cart (mobile) : reveal when main ATC scrolls away ---- */
  const stickyBar = document.querySelector('[data-sticky-atc]');
  const mainForm = document.querySelector('[data-atc-anchor]');
  if (stickyBar && mainForm && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => stickyBar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0));
      },
      { threshold: 0 }
    );
    observer.observe(mainForm);
  }

  /* ---- « Prévenez-moi » : ouvre le formulaire Stoq (RestockRocket) via proxy-click ---- */
  // Stoq n'expose pas d'API sur window ; son bouton .restock-rocket-button-product est masqué
  // en CSS mais reste cliquable par programmation → on relaie le clic depuis notre bouton on-brand.
  function openStoqNotifyForm() {
    const stoqBtn = document.querySelector(
      '.restock-rocket-button-product, .restock-rocket-button, .restock-rocket-button-container button'
    );
    if (stoqBtn) {
      stoqBtn.click();
      return true;
    }
    return false;
  }
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest && e.target.closest('[data-notify]');
    if (!trigger) return;
    e.preventDefault();
    if (openStoqNotifyForm()) return;
    // Stoq pas encore injecté (rare course au chargement) → bref réessai.
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (openStoqNotifyForm() || tries > 10) clearInterval(timer);
    }, 200);
  });
})();
